/**
 * useMarketRegime — globaler Hook, der SPY/BTC/TLT/GLD und die wichtigsten
 * Sektor-ETFs laufend überwacht und einen erweiterten Regime-Snapshot zurückgibt.
 *
 * - Aktien-Regime aus SPY+MA200
 * - Krypto-Regime aus BTC+MA200
 * - Bonds-Regime aus TLT (steigt = Risk-Off-Signal)
 * - Edelmetalle aus GLD (steigt = Inflation/Krise)
 * - Sektor-Rotation: relative 26W-Returns der 8 SPDR-Sektoren vs. SPY
 *
 * Lange TTL (15-30 Min), weil Regime sich nicht minütlich ändert.
 */

import { useMemo } from 'react';

import { useDataState } from './use-data-state';
import { getMarketDataProvider } from '@/lib/market-data';
import { computeOhlcvFeatures, type OhlcvFeatures } from '@/lib/scoring/ohlcv-features';
import { classifyRegime } from '@/lib/scoring/market-regime';
import {
  SECTOR_ETFS,
  computeSectorRotation,
  type SectorRotationSnapshot,
} from '@/lib/scoring/sector-rotation';
import { supabase } from '@/lib/supabase';
import type { AssetClass } from '@/types';
import type { MarketRegime } from '@/lib/scoring/types';
import type { PriceQuote } from '@/lib/market-data/types';

const STOCKS_PROXY = 'SPY';
const CRYPTO_PROXY = 'BTC/USD';
const BONDS_PROXY = 'TLT';
const METALS_PROXY = 'GLD';

export interface VixSnapshot {
  vix_close: number;
  vix_20d_high: number;
  as_of_date: string;
}

interface UseMarketRegimeResult {
  stocks: MarketRegime | null;
  crypto: MarketRegime | null;
  bonds: MarketRegime | null;
  metals: MarketRegime | null;
  sectorRotation: SectorRotationSnapshot | null;
  /** Geldfluss-Radar — rohe Quotes (SPY, BTC, TLT, GLD + 8 Sektoren), damit
   *  weitere Konsumenten keine Doppel-Fetches machen müssen. */
  rawQuotes: Map<string, PriceQuote | null>;
  /** SPY-OHLCV-Features inkl. ADX(14) — für MarketPhaseInput. */
  spyFeatures: OhlcvFeatures | null;
  /** SPY-Quote — für MarketPhaseInput. */
  spyPrice: number | null;
  /** Letzter VIX-Close + 20d-High aus v5_vix_history. */
  vix: VixSnapshot | null;
  forAssetClass: (asset_class: AssetClass) => MarketRegime | null;
  isLoading: boolean;
}

export function useMarketRegime(): UseMarketRegimeResult {
  const provider = useMemo(() => getMarketDataProvider(), []);

  // ---- Aktien (SPY) ----
  const spyQuote = useDataState({
    queryKey: ['regime-quote', STOCKS_PROXY, provider.name],
    queryFn: (signal) => provider.getQuote(STOCKS_PROXY, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 15 * 60,
    isEmpty: (q) => !q || q.current_price === 0,
  });

  const spyOhlcv = useDataState({
    queryKey: ['regime-ohlcv', STOCKS_PROXY, provider.name],
    queryFn: (signal) =>
      provider.getOhlcv(STOCKS_PROXY, 'daily', 250, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 30 * 60,
    isEmpty: (b) => !b || b.length === 0,
  });

  // ---- Krypto (BTC) ----
  const btcQuote = useDataState({
    queryKey: ['regime-quote', CRYPTO_PROXY, provider.name],
    queryFn: (signal) => provider.getQuote(CRYPTO_PROXY, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 15 * 60,
    isEmpty: (q) => !q || q.current_price === 0,
  });

  const btcOhlcv = useDataState({
    queryKey: ['regime-ohlcv', CRYPTO_PROXY, provider.name],
    queryFn: (signal) =>
      provider.getOhlcv(CRYPTO_PROXY, 'daily', 250, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 30 * 60,
    isEmpty: (b) => !b || b.length === 0,
  });

  // ---- Bonds (TLT) ----
  const tltQuote = useDataState({
    queryKey: ['regime-quote', BONDS_PROXY, provider.name],
    queryFn: (signal) => provider.getQuote(BONDS_PROXY, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 15 * 60,
    isEmpty: (q) => !q || q.current_price === 0,
  });

  const tltOhlcv = useDataState({
    queryKey: ['regime-ohlcv', BONDS_PROXY, provider.name],
    queryFn: (signal) =>
      provider.getOhlcv(BONDS_PROXY, 'daily', 250, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 30 * 60,
    isEmpty: (b) => !b || b.length === 0,
  });

  // ---- Edelmetalle (GLD) ----
  const gldQuote = useDataState({
    queryKey: ['regime-quote', METALS_PROXY, provider.name],
    queryFn: (signal) => provider.getQuote(METALS_PROXY, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 15 * 60,
    isEmpty: (q) => !q || q.current_price === 0,
  });

  const gldOhlcv = useDataState({
    queryKey: ['regime-ohlcv', METALS_PROXY, provider.name],
    queryFn: (signal) =>
      provider.getOhlcv(METALS_PROXY, 'daily', 250, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 30 * 60,
    isEmpty: (b) => !b || b.length === 0,
  });

  // ---- VIX (aus v5_vix_history, Server-befüllt via refresh-vix Cron) ----
  const vixData = useDataState<VixSnapshot | null>({
    queryKey: ['regime-vix-history'],
    queryFn: async () => {
      const { data } = await supabase
        .from('v5_vix_history')
        .select('vix_close, vix_20d_high, as_of_date')
        .order('as_of_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!data) return null;
      return data as VixSnapshot;
    },
    enabled: true,
    ttlSeconds: 30 * 60,
    isEmpty: (v) => !v,
  });

  // ---- Sektor-ETFs (für Rotation-Analyse) — nur Quotes, keine OHLCV ----
  // Wir bauen einen separaten Hook pro ETF, weil useDataState pro Symbol einen
  // eigenen Schlüssel braucht. Acht ETFs sind händelbar, TTL ist 30 Min.
  const sectorQuotes = useSectorQuotes(provider);

  // ---- Klassifikation ----
  const spyFeatures = useMemo(() => {
    return spyOhlcv.state.data ? computeOhlcvFeatures(spyOhlcv.state.data) : null;
  }, [spyOhlcv.state.data]);

  const stocksRegime = useMemo(() => {
    if (!spyQuote.state.data) return null;
    return classifyRegime(spyQuote.state.data, spyFeatures, 'stocks');
  }, [spyQuote.state.data, spyFeatures]);

  const cryptoRegime = useMemo(() => {
    if (!btcQuote.state.data) return null;
    const features = btcOhlcv.state.data ? computeOhlcvFeatures(btcOhlcv.state.data) : null;
    return classifyRegime(btcQuote.state.data, features, 'crypto');
  }, [btcQuote.state.data, btcOhlcv.state.data]);

  const bondsRegime = useMemo(() => {
    if (!tltQuote.state.data) return null;
    const features = tltOhlcv.state.data ? computeOhlcvFeatures(tltOhlcv.state.data) : null;
    return classifyRegime(tltQuote.state.data, features, 'bonds');
  }, [tltQuote.state.data, tltOhlcv.state.data]);

  const metalsRegime = useMemo(() => {
    if (!gldQuote.state.data) return null;
    const features = gldOhlcv.state.data ? computeOhlcvFeatures(gldOhlcv.state.data) : null;
    return classifyRegime(gldQuote.state.data, features, 'metals');
  }, [gldQuote.state.data, gldOhlcv.state.data]);

  const sectorRotation = useMemo<SectorRotationSnapshot | null>(() => {
    if (!spyQuote.state.data) return null;
    const map = new Map<string, PriceQuote | null>();
    for (const [sym, q] of sectorQuotes) {
      map.set(sym, q);
    }
    // Welle 8 — Krypto als 9. Reihe in die Sektor-Rotation aufnehmen.
    return computeSectorRotation(spyQuote.state.data, map, btcQuote.state.data ?? null);
  }, [spyQuote.state.data, sectorQuotes, btcQuote.state.data]);

  // Geldfluss-Radar — alle bereits geladenen Quotes als Map durchreichen.
  const rawQuotes = useMemo(() => {
    const m = new Map<string, PriceQuote | null>(sectorQuotes);
    m.set(STOCKS_PROXY, spyQuote.state.data ?? null);
    m.set(CRYPTO_PROXY, btcQuote.state.data ?? null);
    m.set(BONDS_PROXY, tltQuote.state.data ?? null);
    m.set(METALS_PROXY, gldQuote.state.data ?? null);
    return m;
  }, [
    sectorQuotes,
    spyQuote.state.data,
    btcQuote.state.data,
    tltQuote.state.data,
    gldQuote.state.data,
  ]);

  const forAssetClass = (asset_class: AssetClass): MarketRegime | null => {
    if (asset_class === 'crypto') return cryptoRegime;
    return stocksRegime; // stock + etf + commodity_etf
  };

  const isLoading =
    spyQuote.state.status === 'loading' || btcQuote.state.status === 'loading';

  return {
    stocks: stocksRegime,
    crypto: cryptoRegime,
    bonds: bondsRegime,
    metals: metalsRegime,
    sectorRotation,
    rawQuotes,
    spyFeatures,
    spyPrice: spyQuote.state.data?.current_price ?? null,
    vix: vixData.state.data ?? null,
    forAssetClass,
    isLoading,
  };
}

/**
 * Liefert eine Map symbol -> PriceQuote für alle Sektor-ETFs.
 * Nutzt parallele useDataState-Hooks.
 */
function useSectorQuotes(
  provider: ReturnType<typeof getMarketDataProvider>
): Map<string, PriceQuote | null> {
  // useDataState muss in stabiler Reihenfolge aufgerufen werden — wir
  // iterieren also über die fixe SECTOR_ETFS-Liste und sammeln die Hooks.
  const xlk = useSectorQuote(provider, SECTOR_ETFS[0]!.symbol);
  const xly = useSectorQuote(provider, SECTOR_ETFS[1]!.symbol);
  const xli = useSectorQuote(provider, SECTOR_ETFS[2]!.symbol);
  const xlp = useSectorQuote(provider, SECTOR_ETFS[3]!.symbol);
  const xlu = useSectorQuote(provider, SECTOR_ETFS[4]!.symbol);
  const xlv = useSectorQuote(provider, SECTOR_ETFS[5]!.symbol);
  const xlf = useSectorQuote(provider, SECTOR_ETFS[6]!.symbol);
  const xle = useSectorQuote(provider, SECTOR_ETFS[7]!.symbol);

  return useMemo(() => {
    const m = new Map<string, PriceQuote | null>();
    m.set(SECTOR_ETFS[0]!.symbol, xlk);
    m.set(SECTOR_ETFS[1]!.symbol, xly);
    m.set(SECTOR_ETFS[2]!.symbol, xli);
    m.set(SECTOR_ETFS[3]!.symbol, xlp);
    m.set(SECTOR_ETFS[4]!.symbol, xlu);
    m.set(SECTOR_ETFS[5]!.symbol, xlv);
    m.set(SECTOR_ETFS[6]!.symbol, xlf);
    m.set(SECTOR_ETFS[7]!.symbol, xle);
    return m;
  }, [xlk, xly, xli, xlp, xlu, xlv, xlf, xle]);
}

function useSectorQuote(
  provider: ReturnType<typeof getMarketDataProvider>,
  symbol: string
): PriceQuote | null {
  const result = useDataState({
    queryKey: ['regime-sector-quote', symbol, provider.name],
    queryFn: (signal) => provider.getQuote(symbol, signal).then((s) => s.data),
    enabled: provider.is_live,
    ttlSeconds: 30 * 60,
    isEmpty: (q) => !q || q.current_price === 0,
  });
  return result.state.data ?? null;
}
